import React, { useContext, useRef, useState } from 'react';

import { RightContext } from '@/components/RightProvider/context';

interface UploadProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  accept?: string;
  disabled?: boolean;
  /**
   * 是否需要登录校验，默认为 true
   * 设置为 false 则跳过登录检查，直接允许上传
   */
  requireLogin?: boolean;
  onFileChange: (files: File[]) => void | Promise<void>;
  onDrop?: (e: React.DragEvent<HTMLDivElement>) => void;
}

const Upload = (props: UploadProps) => {
  const {
    className,
    accept,
    disabled,
    requireLogin = true,
    children,
    onClick,
    onFileChange,
    onDrop,
  } = props;

  const rightContext = useContext(RightContext);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = async (e: any) => {
    if (disabled) return;

    // 不需要登录，直接触发文件选择
    if (!requireLogin) {
      if (inputRef.current) {
        inputRef.current.value = '';
        inputRef.current.click();
        onClick && onClick(e);
      }
      return;
    }

    // 需要登录，先检查登录状态
    try {
      await rightContext.getUserLoginState({
        onReady() {
          // 登录成功后，触发文件选择器
          if (inputRef.current) {
            inputRef.current.value = '';
            inputRef.current.click();
          }
          onClick && onClick(e);
        },
      });

      // 未登录或取消登录，不做任何操作
      // onReady 回调会在登录成功后自动触发文件选择
    } catch (error) {
      console.error('Login check failed:', error);
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;

    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    // 不需要登录，直接处理拖拽
    if (!requireLogin) {
      onDrop && onDrop(e);
      return;
    }

    // 需要登录，先检查登录状态
    try {
      await rightContext.getUserLoginState({
        onReady() {
          // 登录成功后，让父组件处理拖拽的文件
          onDrop && onDrop(e);
        },
      });
      // 未登录或取消登录，不做任何操作
      // onReady 回调会在登录成功后自动处理文件
    } catch (error) {
      console.error('Login check failed:', error);
    }
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  return (
    <div
      onClick={handleClick}
      onDrop={handleDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      className={`${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${dragOver && 'opacity-70'} ${className}`}
    >
      <input
        type="file"
        ref={inputRef}
        accept={accept}
        multiple={true}
        className="!hidden"
        onClick={(e) => {
          e.stopPropagation();
        }}
        onChange={(e) => {
          if (e.target.files?.length) {
            onFileChange(Array.from(e.target.files));
          } else {
            onFileChange([]);
          }
        }}
      />
      {children}
    </div>
  );
};

export default Upload;
